import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentListTable } from "@/components/payments/PaymentListTable";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  CreditCard,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { usePaymentsList } from "@/hooks/usePaymentQueries";
import type {
  PaymentMethod,
  PaymentStatus,
} from "@/services/admin-payment.service";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import {
  ALL,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  usePaymentFiltersStore,
} from "@/stores/paymentFiltersStore";
import { getPaginationItems } from "@/lib/pagination";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import {
  PAYMENT_COLUMNS,
  usePaymentColumnsStore,
} from "@/stores/paymentColumnsStore";

export function PaymentListPage() {
  const navigate = useNavigate();

  // Filters, page and page size live in a persisted store, so the view an operator set up survives
  // a reload and a trip to a payment's detail screen and back.
  const { search, status, paymentMethod, dateRange } = usePaymentFiltersStore(
    (state) => state.filters,
  );
  const page = usePaymentFiltersStore((state) => state.page);
  const pageSize = usePaymentFiltersStore((state) => state.pageSize);
  const setFilter = usePaymentFiltersStore((state) => state.setFilter);
  const setPage = usePaymentFiltersStore((state) => state.setPage);
  const setPageSize = usePaymentFiltersStore((state) => state.setPageSize);
  const resetFilters = usePaymentFiltersStore((state) => state.resetFilters);
  const hasActiveFilters = usePaymentFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // The table subscribes to this store itself; the page only needs what the menu renders from.
  const hiddenColumns = usePaymentColumnsStore((state) => state.hidden);
  const toggleColumn = usePaymentColumnsStore((state) => state.toggle);
  const showAllColumns = usePaymentColumnsStore((state) => state.showAll);

  // Bumped by Refresh to clear the table's row selection. A counter rather than a callback so the
  // page never has to hold the selection it does not own.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  // Only the half-typed term is local: committing per keystroke would re-query and write
  // sessionStorage per character.
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Debounce free-text search.
  useEffect(() => {
    // Trim before comparing and before pushing: the backend treats a whitespace-only term as no
    // filter, so it must not produce its own query key and request.
    const trimmed = searchInput.trim();

    const timer = setTimeout(() => {
      // Guarded because this also runs on mount, where committing the restored value would be a
      // no-op write that still sends the operator back to page 1.
      if (trimmed !== search) {
        setFilter("search", trimmed);
      }
    }, 750);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  // Both ends or neither: the picker never produces half a range, and half a range narrows nothing
  // while still rendering an active-filter chip.
  const { fromDate, toDate } =
    dateRange.from && dateRange.to
      ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  const filters = {
    search: search || undefined,
    status: status !== ALL ? status : undefined,
    paymentMethod: paymentMethod !== ALL ? paymentMethod : undefined,
    fromDate,
    toDate,
    page,
    pageSize,
  };

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = usePaymentsList(filters);

  // This screen's "start over", the same role the resource list gives its Refresh: it drops every
  // filter and reloads. It became the reset when the separate "Clear filters" button was removed —
  // with the chips as the only other way back, a Refresh that left the search term sitting in the
  // box reloaded a view the operator was trying to leave.
  //
  // `resetFilters` and not `clearCache`: the filters and the page go back to their defaults, but
  // the page size the operator chose is theirs and survives. The two text inputs hold their own
  // copy of what is being typed, so clearing the store alone would leave the old term on screen —
  // which is exactly the bug this fixes.
  //
  // It drops the row selection too: a tick that survives a reset says "cancel this" about a row
  // chosen against filters that no longer apply. The pruning effect in the table only covers rows
  // that leave the page — one still listed afterwards keeps its tick without this.
  const handleRefresh = () => {
    setSearchInput("");
    resetFilters();
    setSelectionResetKey((key) => key + 1);
    void refetch();
  };

  const handleViewDetails = (paymentId: string) => {
    navigate(`/admin/payments/${paymentId}`);
  };

  // A term typed but not yet committed: the debounce has not fired, so nothing is fetching and the
  // table still shows the previous result. The spinner in the search box reads from this.
  const isSearchPending = searchInput !== search;

  const payments = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;

  // The store's page, not the response's: this query keeps no placeholder data, so while a page
  // change is in flight `response` is undefined and reading the page number off it fell back to 1 -
  // the label said "Page 1", the row numbering restarted at 1, and the highlighted page button
  // jumped back to the first one for the length of the request.
  const currentPage = page;
  const totalCount = response?.pagination?.totalItems || 0;

  // Only take the screen over when there is genuinely nothing to show. A refetch that fails — a
  // token refresh losing a race, a blip on one filter change — used to replace the table AND the
  // whole filter bar with a "Retry" card, so the operator had to rebuild their filters to get back
  // to rows that were still on screen a moment earlier. With a response in hand the failure is
  // reported in a banner above the table instead, and Retry is one click away either way.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">Failed to load payments</p>
              <Button
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-4"
              >
                {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // A filter or page change drops the previous rows on purpose (`usePaymentsList` keeps no
  // placeholder data), so there is a stretch with nothing to render. Naming it keeps the two
  // loading signals apart: skeleton rows while the table is empty, the "Updating..." pill only
  // when it is refreshing rows the operator can still see.
  const isReloading = isFetching && payments.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport: `fixed inset-0` centred this on the screen, so
            on a short table it hovered in the blank space well below the rows it described. */}
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          {/* Filters Toolbar */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {/* The debounce window has no isFetching to report, so the box says so itself:
                    a term typed but not yet committed leaves the table showing the previous
                    result, and without this the screen sits motionless for three quarters of a
                    second after every keystroke and reads as frozen. */}
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  placeholder="Search payment no., order no., ID, email"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  // React withholds onChange while an IME composition is open — which is the whole
                  // time a Vietnamese Telex/VNI method is assembling a word — so the term only
                  // reached state once a space closed the composition, and the search appeared to
                  // need a trailing space to run at all.
                  onCompositionEnd={(e) =>
                    setSearchInput(e.currentTarget.value)
                  }
                  className="pl-10 pr-9"
                />
              </div>
            </div>

            {/* Sits directly after the search box rather than at the far end of the toolbar: it is
                the only control here that leads somewhere instead of narrowing the list, and next
                to the input it reads as the screen's own action rather than the last of three
                icons. shrink-0 keeps its caption whole when the active-filter chips beside it fill
                the row. Same placement as New Tenant on the tenant list. */}
            <Button
              onClick={() => navigate("/admin/payments/verification")}
              variant="outline"
              size="sm"
              className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
            >
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Verification Queue
            </Button>

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setFilter("search", "");
                    }}
                    aria-label="Clear search filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {status !== ALL && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status:{" "}
                  {
                    PAYMENT_STATUS_OPTIONS.find((o) => o.value === status)
                      ?.label
                  }
                  <button
                    type="button"
                    onClick={() => setFilter("status", ALL)}
                    aria-label="Clear status filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {paymentMethod !== ALL && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Method:{" "}
                  {
                    PAYMENT_METHOD_OPTIONS.find(
                      (o) => o.value === paymentMethod,
                    )?.label
                  }
                  <button
                    type="button"
                    onClick={() => setFilter("paymentMethod", ALL)}
                    aria-label="Clear method filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {dateRange.from && dateRange.to && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Created: {formatDate(dateRange.from)} –{" "}
                  {formatDate(dateRange.to)}
                  <button
                    type="button"
                    onClick={() => setFilter("dateRange", {})}
                    aria-label="Clear created date filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover>
              {/* Icon only, so the accessible name has to come from somewhere: `title` gives the
                  hover tooltip, `aria-label` gives screen readers the same words. The comment sits
                  outside PopoverTrigger on purpose: `asChild` forwards to a single child. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentStatusFilter">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setFilter("status", value as PaymentStatus | typeof ALL)
                    }
                  >
                    <SelectTrigger id="paymentStatusFilter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentMethodFilter">Payment Method</Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={(value) =>
                      setFilter(
                        "paymentMethod",
                        value as PaymentMethod | typeof ALL,
                      )
                    }
                  >
                    <SelectTrigger id="paymentMethodFilter">
                      <SelectValue placeholder="Payment Method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHOD_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentCreatedDateFilter">Created Date</Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => setFilter("dateRange", range)}
                    placeholder="Select date range"
                  />
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={PAYMENT_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isReloading ? (
            <PaymentListTable
              payments={payments}
              startIndex={(currentPage - 1) * pageSize}
              isLoading
              selectionResetKey={selectionResetKey}
              onViewDetails={handleViewDetails}
            />
          ) : payments.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              {/* Fills the card the way the table does instead of collapsing to its own
                  content. CardContent is a min-h-0 flex column, so a fixed-height block here
                  left the card ending part-way down the screen: the whole panel changed size
                  the moment a filter emptied the list, and changed back when it matched
                  again. justify-center then puts the message in the middle of the space the
                  rows occupied rather than tucked under the toolbar. */}
              <CreditCard className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No results found" : "No payments found"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Payment transactions will appear here once created."}
              </p>
            </div>
          ) : (
            <PaymentListTable
              payments={payments}
              startIndex={(currentPage - 1) * pageSize}
              selectionResetKey={selectionResetKey}
              onViewDetails={handleViewDetails}
            />
          )}

          {/* The footer sits outside the three branches above, so a search that matched nothing
              still shows its pager, its page-size picker and the row count. It reads
              "Page 1 of 1 (0 total)" with both arrows disabled - a true statement about a result
              that came back, and the row an operator needs to change the page size or step back a
              page after a filter emptied the list.

              `!isReloading` is the one case still held back: while the rows are on their way
              `response` is undefined (usePaymentsList keeps no placeholder data), so "0 total"
              would describe a request that has not answered yet - the same lie the skeleton above
              exists to avoid. */}
          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed, and a
                    search that matched nothing lost the page-size picker with them. Previous and
                    Next disable themselves at the ends, so a single page simply shows both greyed
                    out next to page 1. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls, the same
                    place the resource list keeps it. The label is gone, so this tooltip is the
                    only thing left telling the operator that the click throws their filters away —
                    so it says exactly that, the same wording the resource list uses. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
